import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// libraries
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    alpha,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
// components
import Iconify from '../Iconify';
import { FormProvider, RHFTextField } from '../hook-form';
import ReportsAudienceForm from '../ReportsAudienceForm';
import { Transition } from '.';
// redux
import { dispatch, useSelector } from '../../redux/store';
import { updateError } from '../../redux/slices/user';
import {
    handleCreateAudience,
    saveNewAudience,
    setHeaders,
    updateSelectedAudience,
} from '../../redux/slices/audienceManager';
import { updateConditionList } from '../../redux/slices/campaign';
// utils
import { callApi } from '../../utils/apiCall';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// validations
import { AudienceCampaignSchema } from '../../validations/validations';
// styles
import { grey } from '../../theme/palette';
import {
    createScriptButtonStyle,
    saveButtonStyle,
    cancelButtonStyle,
    reportsAudienceStack,
    reportsAudienceTitleWrapper,
} from '../../styles/AudienceManager';
import { leadListingBox, reminderStackStyle } from '../../styles/AddReminderPopup';

/**
 * Debounced effect helper — runs `fn` after `delay` ms of no change in deps.
 * Used for the live count query so we don't flood the API on every keystroke.
 */
const useDebouncedEffect = (fn, deps, delay) => {
    useEffect(() => {
        const id = setTimeout(fn, delay);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
};

// Small coloured pill used in the purpose banner to echo the popup's filters
// back to the user (e.g. "Consultation Attended · Invisalign · Last 30 days")
const ContextChip = ({ icon, label }) => (
    <Chip
        size="small"
        icon={<Iconify icon={icon} width={14} />}
        label={label}
        sx={{
            height: 22,
            fontSize: '11px',
            fontWeight: 600,
            bgcolor: '#ede9fe',
            color: '#5119B7',
            '& .MuiChip-icon': { color: '#5119B7', ml: '6px', mr: '-4px' },
        }}
    />
);
ContextChip.propTypes = { icon: PropTypes.string.isRequired, label: PropTypes.string.isRequired };

// Section header for the numbered steps (1 · 2 · 3) — keeps the UI legible as
// a guided task instead of a wall of fields.
const StepHeader = ({ number, title, hint }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
        <Box
            sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: '#7c3aed',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.25,
            }}
        >
            {number}
        </Box>
        <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{title}</Typography>
            {hint && (
                <Typography sx={{ fontSize: '12px', color: grey[600] }}>{hint}</Typography>
            )}
        </Box>
    </Box>
);
StepHeader.propTypes = {
    number: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    hint: PropTypes.string,
};
StepHeader.defaultProps = { hint: '' };

const ReportsAudienceDialog = ({
    generateAudience,
    currentTab,
    handleCreateAudienceSuccess,
    handleCloseAudienceDialog,
    popupContext,
}) => {
    const navigate = useNavigate();
    const defaultValues = { audienceName: '' };

    const { newAudience, isAudienceIncludeArchive, selectedAudienceData } = useSelector(
        (state) => state.audienceManager
    );

    const methods = useForm({
        resolver: yupResolver(AudienceCampaignSchema),
        defaultValues,
    });
    const { reset, handleSubmit, watch } = methods;
    const currentName = watch('audienceName');

    // Post-save confirmation state — once the audience lands, we swap to a
    // success view with an actionable "Send a Campaign" CTA so the user can
    // immediately use what they just built.
    const [successView, setSuccessView] = useState({ open: false, audienceId: null, audienceName: '' });

    // Live count preview state — debounced fetch of matching lead count so the
    // user sees impact before clicking Save.
    const [liveCount, setLiveCount] = useState({ loading: false, count: null });

    const AUDIENCE_TABS = [
        { value: 'By Conditions', component: <ReportsAudienceForm /> },
    ];

    const getFilters = () => ({
        filters: {
            ...(newAudience.tagged_items__tag__name__in ||
                selectedAudienceData?.recepient?.filters?.tagged_items__tag__name__in
                ? newAudience.tagged_items__tag__name__in || {
                    tagged_items__tag__name__in: selectedAudienceData?.recepient?.filters.tagged_items__tag__name__in,
                }
                : {}),
            ...(newAudience.lead_treatments__status__in ||
                selectedAudienceData?.recepient?.filters?.lead_treatments__status__in
                ? newAudience.lead_treatments__status__in || {
                    lead_treatments__status__in: selectedAudienceData?.recepient?.filters?.lead_treatments__status__in,
                }
                : {}),
            ...(newAudience.lead_treatments__treatment__treatment_item__name__in ||
                selectedAudienceData?.recepient?.filters?.lead_treatments__treatment__treatment_item__name__in
                ? newAudience.lead_treatments__treatment__treatment_item__name__in || {
                    lead_treatments__treatment__treatment_item__name__in:
                        selectedAudienceData?.recepient?.filters?.lead_treatments__treatment__treatment_item__name__in,
                }
                : {}),
            ...(newAudience.assignees__user__id__in || selectedAudienceData?.recepient?.filters?.assignees__user__id__in
                ? newAudience.assignees__user__id__in || {
                    assignees__user__id__in: selectedAudienceData?.recepient?.filters?.assignees__user__id__in,
                }
                : {}),
            ...(newAudience.lead_source__referral__in || selectedAudienceData?.recepient?.filters?.lead_source__referral__in
                ? newAudience.lead_source__referral__in || {
                    lead_source__referral__in: selectedAudienceData?.recepient?.filters?.lead_source__referral__in,
                }
                : {}),
            ...(newAudience.created_at__range || selectedAudienceData?.recepient?.filters?.created_at__range
                ? newAudience.created_at__range || {
                    created_at__range: selectedAudienceData?.recepient?.filters?.created_at__range,
                }
                : {}),
            ...(newAudience.lead_source__type__in || selectedAudienceData?.recepient?.filters?.lead_source__type__in
                ? newAudience.lead_source__type__in || {
                    lead_source__type__in: selectedAudienceData?.recepient?.filters?.lead_source__type__in,
                }
                : {}),
            ...(newAudience.lead_treatments__treatment__treatment_item__treatment_category__in || selectedAudienceData?.recepient?.filters?.lead_treatments__treatment__treatment_item__treatment_category__in
                ? newAudience.lead_treatments__treatment__treatment_item__treatment_category__in || {
                    lead_treatments__treatment__treatment_item__treatment_category__in: selectedAudienceData?.recepient?.filters?.lead_treatments__treatment__treatment_item__treatment_category__in,
                }
                : {}),
        },
        is_archived_included: isAudienceIncludeArchive,
    });

    const isNext = () =>
        newAudience?.tagsNames?.length > 0 ||
        newAudience?.treatmentName?.length > 0 ||
        newAudience?.statusNames?.length > 0 ||
        newAudience?.archivedValue ||
        newAudience?.practitionerName?.length > 0 ||
        newAudience?.referralName?.length > 0 ||
        newAudience?.dateRange?.length > 0 ||
        newAudience?.source__type?.length > 0 ||
        newAudience?.category__in?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.tagged_items__tag__name__in?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.lead_treatments__treatment__treatment_item__name__in?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.lead_treatments__status__in?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.is_only_archived ||
        selectedAudienceData?.recepient?.filters?.assignees__user__id__in?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.lead_source__referral__in?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.created_at__range?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.lead_treatments__treatment__treatment_item__treatment_category__in?.length > 0 ||
        selectedAudienceData?.recepient?.filters?.lead_source__type__in?.length > 0;

    // Pre-fill the audience state from the popup's current filters so the user
    // doesn't have to re-select what they were just looking at.
    // Runs once when the dialog opens + context is available.
    useEffect(() => {
        if (!generateAudience.value || !popupContext) return;
        const prefill = {};
        if (popupContext.status && popupContext.status !== 'NEW' && popupContext.status !== 'ARCHIVED') {
            prefill.lead_treatments__status__in = { lead_treatments__status__in: [popupContext.status] };
            prefill.statusNames = [popupContext.status];
        }
        if (popupContext.treatmentName && popupContext.treatmentName !== 'All Leads') {
            prefill.lead_treatments__treatment__treatment_item__name__in = {
                lead_treatments__treatment__treatment_item__name__in: [popupContext.treatmentName],
            };
            prefill.treatmentName = [popupContext.treatmentName];
        }
        if (popupContext.dateRange?.startDate && popupContext.dateRange?.endDate) {
            const start = moment(popupContext.dateRange.startDate).format('YYYY-MM-DD');
            const end = moment(popupContext.dateRange.endDate).format('YYYY-MM-DD');
            const range = [start, end];
            prefill.created_at__range = { created_at__range: range };
            prefill.dateRange = range;
        }
        if (Object.keys(prefill).length > 0) {
            dispatch(saveNewAudience(prefill));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generateAudience.value]);

    // Human-readable context summary for the banner
    const contextChips = useMemo(() => {
        if (!popupContext) return [];
        const chips = [];
        if (popupContext.statusLabel) chips.push({ icon: 'mdi:tag-outline', label: popupContext.statusLabel });
        if (popupContext.treatmentName && popupContext.treatmentName !== 'All Leads') {
            chips.push({ icon: 'mdi:tooth-outline', label: popupContext.treatmentName });
        }
        if (popupContext.dateRange?.startDate && popupContext.dateRange?.endDate) {
            const s = moment(popupContext.dateRange.startDate).format('DD MMM');
            const e = moment(popupContext.dateRange.endDate).format('DD MMM');
            chips.push({ icon: 'mdi:calendar-range', label: `${s} – ${e}` });
        }
        return chips;
    }, [popupContext]);

    // Debounced live count — posts the full filter dict to the audience
    // preview endpoint in one request. Previously hit /leads/?aggregate=Count
    // with each filter URL-encoded individually, which was both noisy (many
    // coalesced requests on the GET dedup) and hard to extend (each new
    // filter type needed custom URL-building here). The preview endpoint
    // mirrors AudienceInternalQueryset resolution so the count the user
    // sees here is exactly the count they'll get after saving. Falls back
    // to the legacy per-filter GET for older API deploys.
    useDebouncedEffect(() => {
        if (!generateAudience.value || !popupContext?.practiceId) return;
        const { filters } = getFilters();
        const hasAnyFilter = Object.keys(filters).length > 0;
        if (!hasAnyFilter) { setLiveCount({ loading: false, count: null }); return; }

        setLiveCount((prev) => ({ ...prev, loading: true }));
        callApi(
            `/api/v1/practices/${popupContext.practiceId}/audiences/preview-count/`,
            {
                method: 'POST',
                body: {
                    filters,
                    is_archived_included: !!isAudienceIncludeArchive,
                    is_only_archived: false,
                },
            },
        )
            .then((res) => {
                if (res?.ok && typeof res?.data?.count === 'number') {
                    setLiveCount({ loading: false, count: res.data.count });
                    return;
                }
                // Backend doesn't have preview-count yet (404 / 405). Fall
                // back to the legacy per-filter GET so older deploys still
                // show a live count.
                const params = [];
                if (filters.lead_treatments__status__in) {
                    const v = filters.lead_treatments__status__in;
                    params.push(`lead_treatments__status__in=${Array.isArray(v) ? v.join(',') : v}`);
                }
                if (filters.lead_treatments__treatment__treatment_item__name__in) {
                    const v = filters.lead_treatments__treatment__treatment_item__name__in;
                    const encoded = (Array.isArray(v) ? v : [v]).map(encodeURIComponent).join(',');
                    params.push(`lead_treatments__treatment__treatment_item__name__in=${encoded}`);
                }
                if (filters.created_at__range && Array.isArray(filters.created_at__range)) {
                    const [s, e] = filters.created_at__range;
                    params.push(`created_at__gte=${s}&created_at__lte=${e} 23:59:59`);
                }
                if (filters.tagged_items__tag__name__in) {
                    const v = filters.tagged_items__tag__name__in;
                    const encoded = (Array.isArray(v) ? v : [v]).map(encodeURIComponent).join(',');
                    params.push(`tags__name__in=${encoded}`);
                }
                if (isAudienceIncludeArchive) params.push('is_archived_included=true');
                callApi(
                    `/api/v1/practices/${popupContext.practiceId}/leads/?aggregate[Count]=id&${params.join('&')}`
                ).then((legacy) => {
                    const raw = legacy?.data?.data;
                    const n = Array.isArray(raw) && raw[0]?.count_id ? raw[0].count_id : (legacy?.data?.count ?? 0);
                    setLiveCount({ loading: false, count: n });
                }).catch(() => setLiveCount({ loading: false, count: null }));
            })
            .catch(() => setLiveCount({ loading: false, count: null }));
    }, [generateAudience.value, JSON.stringify(newAudience), isAudienceIncludeArchive, popupContext?.practiceId], 400);

    const handleClose = () => {
        setSuccessView({ open: false, audienceId: null, audienceName: '' });
        handleCloseAudienceDialog();
    };

    const onSubmit = (data) => {
        if (!isNext()) {
            dispatch(
                updateError({
                    message: 'Please select any one tag',
                    status: false,
                })
            );
            return;
        }
        dispatch(setHeaders(null));
        const payloadData = {
            name: data?.audienceName || '',
            type: 'INTERNAL_QUERYSET',
            audience_internal_queryset: getFilters(),
        };
        if (newAudience.is_only_archived || selectedAudienceData?.recepient?.filters?.is_only_archived) {
            if (
                (newAudience.is_only_archived && newAudience?.is_only_archived?.is_open_for_communication === 'false') ||
                (selectedAudienceData?.recepient?.filters?.is_only_archived &&
                    (selectedAudienceData?.recepient?.filters?.is_open_for_communication === 'false' ||
                        selectedAudienceData?.recepient?.filters?.is_open_for_communication === false))
            ) {
                payloadData.audience_internal_queryset.filters = {
                    ...payloadData.audience_internal_queryset.filters,
                    is_only_archived: true,
                    is_open_for_communication: false,
                };
            } else if (newAudience.is_only_archived || selectedAudienceData?.recepient?.filters?.is_only_archived) {
                payloadData.audience_internal_queryset.filters = {
                    ...payloadData.audience_internal_queryset.filters,
                    is_only_archived: true,
                    is_open_for_communication: true,
                };
            }
        }
        const filteredFilters = Object.entries(payloadData?.audience_internal_queryset?.filters)
            .filter(([, value]) => !Array.isArray(value) || value.length > 0)
            .reduce((obj, [key, value]) => {
                obj[key] = value;
                return obj;
            }, {});
        const finalCampaignData = {
            ...payloadData,
            audience_internal_queryset: { ...payloadData?.audience_internal_queryset, filters: filteredFilters },
        };

        // Intercept the success callback so we can show the post-save CTA inline
        // instead of immediately closing the dialog.
        const onSuccess = (createdData) => {
            setSuccessView({ open: true, audienceId: createdData?.id, audienceName: createdData?.name || data.audienceName });
            if (typeof handleCreateAudienceSuccess === 'function') {
                handleCreateAudienceSuccess(createdData);
            }
        };

        dispatch(handleCreateAudience({ payload: finalCampaignData, type: 'AudiencePopup', shouldShowAudienceSuccessMsg: false }, onSuccess));
        reset(defaultValues);
        dispatch(updateSelectedAudience({}));
        dispatch(updateConditionList([]));
    };

    const handleSendCampaign = () => {
        // Navigate to the campaign builder; audience is already saved server-side
        // so the user can pick it in the builder's audience step.
        navigate(PATH_DASHBOARD.campaign.builder);
        handleClose();
    };

    return (
        <div>
            <Dialog TransitionComponent={Transition} open={generateAudience.value} onClose={handleClose} sx={leadListingBox}>
                <Stack sx={{ ...reminderStackStyle, ...reportsAudienceStack }}>
                    <DialogTitle sx={{ padding: '0' }}>
                        <Box sx={reportsAudienceTitleWrapper}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton onClick={handleClose} sx={{ p: 0, mr: 1 }}>
                                    <Iconify height={24} width={24} icon='fa:angle-left' sx={{ color: grey[600] }} />
                                </IconButton>
                                <Typography variant='body' noWrap sx={{ fontSize: '20px' }}>
                                    {successView.open ? 'Audience created' : 'Create audience'}
                                </Typography>
                            </Box>
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ p: 0 }}>
                        {successView.open ? (
                            /* ── Post-save view ──────────────────────────────────────
                             * CTA-first: user just created an audience — the single
                             * highest-value next action is sending a campaign to them. */
                            <Box sx={{ mt: 3, p: 3, borderRadius: '12px', bgcolor: alpha('#10b981', 0.06), border: `1px solid ${alpha('#10b981', 0.3)}`, textAlign: 'center' }}>
                                <Iconify icon='mdi:check-circle' width={56} sx={{ color: '#10b981', mb: 1 }} />
                                <Typography sx={{ fontSize: '16px', fontWeight: 700, mb: 0.5 }}>
                                    {successView.audienceName ? `‘${successView.audienceName}’ is ready` : 'Audience saved'}
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: grey[700], mb: 3 }}>
                                    Find it later in <strong>Campaigns → Audience Manager</strong>. What’s next?
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant='contained'
                                        onClick={handleSendCampaign}
                                        startIcon={<Iconify icon='mdi:send-outline' width={16} />}
                                        sx={{ ...saveButtonStyle, minWidth: 180 }}
                                    >
                                        Send a campaign
                                    </Button>
                                    <Button
                                        variant='outlined'
                                        onClick={handleClose}
                                        sx={{ ...cancelButtonStyle, minWidth: 120 }}
                                    >
                                        Done
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ marginTop: '16px' }}>
                                {/* ── Purpose banner ──────────────────────────────────
                                 * Tells a first-time user what an audience is and where
                                 * it ends up. Also echoes the popup's filters so they
                                 * know the starting conditions are pre-filled. */}
                                <Box sx={{ mb: 3, p: 2, borderRadius: '10px', bgcolor: '#f5f3ff', border: '1px solid #ede9fe' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                                        <Iconify icon='mdi:target-account' width={20} sx={{ color: '#7c3aed', mt: 0.25 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#4c1d95' }}>
                                                A saved list of leads you can message via Email, SMS or WhatsApp.
                                            </Typography>
                                            <Typography sx={{ fontSize: '12px', color: grey[700], mt: 0.25 }}>
                                                Find it later in Campaigns → Audience Manager.
                                                {contextChips.length > 0 ? ' We pre-filled the conditions below from your current view — tweak anything as needed.' : ''}
                                            </Typography>
                                            {contextChips.length > 0 && (
                                                <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                                                    {contextChips.map((c) => (
                                                        <ContextChip key={c.label} icon={c.icon} label={c.label} />
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                                    {/* ── Step 1: Name ────────────────────────────── */}
                                    <StepHeader number={1} title='Name this audience' hint='Give it a short, recognisable name so you can find it in Audience Manager.' />
                                    <Box sx={{ mb: 3 }}>
                                        <RHFTextField
                                            name='audienceName'
                                            label='Audience name'
                                            placeholder='e.g. Invisalign — Attended — Last 30 days'
                                        />
                                    </Box>

                                    {/* ── Step 2: Conditions ──────────────────────── */}
                                    {(() => {
                                        const step2Hint = contextChips.length > 0
                                            ? 'Conditions below are pre-filled from your report view — add / remove to refine.'
                                            : 'Pick one or more conditions to narrow down the leads.';
                                        return (
                                            <StepHeader
                                                number={2}
                                                title='Who should be in this audience?'
                                                hint={step2Hint}
                                            />
                                        );
                                    })()}
                                    <Box sx={{ mb: 3 }}>
                                        {AUDIENCE_TABS.map((tab) => {
                                            const isMatched = tab.value === currentTab;
                                            return isMatched && <Box key={tab.value}>{tab.component}</Box>;
                                        })}
                                    </Box>

                                    {/* ── Step 3: Preview ─────────────────────────── */}
                                    <StepHeader number={3} title='Preview' hint='Live count of leads matching the conditions above.' />
                                    <Box
                                        sx={{
                                            mb: 3,
                                            p: 2,
                                            borderRadius: '10px',
                                            bgcolor: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        {(() => {
                                            if (liveCount.loading) {
                                                return (
                                                    <>
                                                        <CircularProgress size={18} sx={{ color: '#7c3aed' }} />
                                                        <Typography sx={{ fontSize: '13px', color: grey[700] }}>Counting matching leads…</Typography>
                                                    </>
                                                );
                                            }
                                            if (liveCount.count == null) {
                                                return (
                                                    <>
                                                        <Iconify icon='mdi:filter-variant' width={20} sx={{ color: grey[500] }} />
                                                        <Typography sx={{ fontSize: '13px', color: grey[700] }}>
                                                            Add at least one condition above to preview the match count.
                                                        </Typography>
                                                    </>
                                                );
                                            }
                                            return (
                                                <>
                                                    <Iconify icon='mdi:account-group' width={22} sx={{ color: '#7c3aed' }} />
                                                    <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>
                                                        ~{liveCount.count.toLocaleString()} {liveCount.count === 1 ? 'lead' : 'leads'} match
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '12px', color: grey[600] }}>
                                                        · updates as you edit conditions
                                                    </Typography>
                                                </>
                                            );
                                        })()}
                                    </Box>

                                    <Box sx={createScriptButtonStyle}>
                                        <Button variant='outlined' sx={cancelButtonStyle} onClick={handleClose}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type='submit'
                                            variant='contained'
                                            sx={saveButtonStyle}
                                            disabled={!currentName}
                                        >
                                            Save audience
                                        </Button>
                                    </Box>
                                </FormProvider>
                            </Box>
                        )}
                    </DialogContent>
                </Stack>
            </Dialog>
        </div>
    );
};

export default ReportsAudienceDialog;
ReportsAudienceDialog.propTypes = {
    generateAudience: PropTypes.shape({ value: PropTypes.bool, onFalse: PropTypes.func, onTrue: PropTypes.func }),
    currentTab: PropTypes.string,
    handleCreateAudienceSuccess: PropTypes.func,
    handleCloseAudienceDialog: PropTypes.func,
    // Pre-fill context surfaced from the popup that launched this dialog —
    // every field is optional; if absent the dialog falls back to blank form.
    popupContext: PropTypes.shape({
        status: PropTypes.string,
        statusLabel: PropTypes.string,
        treatmentName: PropTypes.string,
        practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        dateRange: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string,
        }),
    }),
};
ReportsAudienceDialog.defaultProps = {
    popupContext: null,
};
